import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CMTComponent = ({ user }) => {
  const [tab, setTab] = useState('send');
  const [searchQuery, setSearchQuery] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [userStats, setUserStats] = useState({});
  const [result, setResult] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [searching, setSearching] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [histRes, statsRes, walletRes] = await Promise.all([
        axios.get(`${API}/cmt/history`),
        axios.get(`${API}/cmt/stats`),
        axios.get(`${API}/wallet`)
      ]);
      setHistory(histRes.data.transfers || []);
      setUserStats(histRes.data.stats || {});
      setStats(statsRes.data || {});
      setWallet({ balance: walletRes.data?.balances?.CUALCOIN || walletRes.data?.balance || 0 });
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchRecipients = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setRecipients([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/cmt/recipients/search?q=${encodeURIComponent(q)}`);
      setRecipients(res.data.recipients || []);
    } catch (err) { setRecipients([]); }
    finally { setSearching(false); }
  };

  const sendTransfer = async () => {
    if (!selectedRecipient || !amount || parseFloat(amount) <= 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/cmt/transfer`, {
        recipient_id: selectedRecipient.id,
        amount: parseFloat(amount),
        note
      });
      setResult(res.data);
      setAmount('');
      setNote('');
      setSelectedRecipient(null);
      setSearchQuery('');
      fetchData();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Error en la transferencia' });
    } finally { setSending(false); }
  };

  const countryFlag = (country) => {
    const flags = { 'VE': 'VE', 'PA': 'PA', 'CO': 'CO', 'BR': 'BR', 'MX': 'MX', 'CL': 'CL', 'AR': 'AR', 'PE': 'PE', 'US': 'US' };
    return flags[country] || country;
  };

  const tabs = [
    { id: 'send', label: 'Enviar', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8' },
    { id: 'history', label: 'Historial', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'stats', label: 'Red CMT', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' }
  ];

  return (
    <div className="space-y-6" data-testid="cmt-module">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"/>
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold">CUALmoneytransfer</h2>
            <p className="text-sm text-emerald-100">Envia dinero a cualquier usuario CUALAPP del mundo</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <p className="text-xs text-emerald-200">Tu saldo</p>
            <p className="text-lg font-bold">{wallet.balance?.toFixed(2)} Cc</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <p className="text-xs text-emerald-200">Enviado</p>
            <p className="text-lg font-bold">{(userStats.total_sent || 0).toFixed(2)} Cc</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
            <p className="text-xs text-emerald-200">Recibido</p>
            <p className="text-lg font-bold">{(userStats.total_received || 0).toFixed(2)} Cc</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cmt-tab-${t.id}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={t.icon} />
            </svg>
            {t.label}
          </button>
        ))}
      </div>

      {/* Send Tab */}
      {tab === 'send' && (
        <div className="space-y-4">
          {/* Success/Error Result */}
          {result && (
            <div className={`p-4 rounded-xl border ${result.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="transfer-result">
              {result.error ? (
                <p className="text-sm text-red-700">{result.error}</p>
              ) : (
                <div>
                  <p className="text-sm font-medium text-emerald-800">{result.message}</p>
                  <div className="flex gap-4 mt-2 text-xs text-emerald-600">
                    <span>ID: {result.transfer_id}</span>
                    {result.fee > 0 && <span>Comision: {result.fee} Cc</span>}
                    {result.is_international && <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded">Internacional</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recipient Search */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-900 mb-2">Destinatario</label>
            {selectedRecipient ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                    {selectedRecipient.avatar_initial}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedRecipient.full_name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{selectedRecipient.email}</span>
                      <span className="px-1.5 py-0.5 bg-white rounded text-emerald-700 font-medium">{countryFlag(selectedRecipient.country)}</span>
                      {selectedRecipient.kyc_verified && <span className="text-emerald-500">Verificado</span>}
                    </div>
                  </div>
                </div>
                <button onClick={() => { setSelectedRecipient(null); setSearchQuery(''); }} data-testid="clear-recipient"
                  className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="relative">
                <input type="text" value={searchQuery} onChange={e => searchRecipients(e.target.value)}
                  placeholder="Buscar por nombre, email o telefono..." data-testid="cmt-search-input"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400" />
                <svg className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                {searching && <div className="absolute right-3 top-3.5 w-4 h-4 border-2 border-emerald-300 border-t-transparent rounded-full animate-spin" />}
                {recipients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 max-h-48 overflow-y-auto" data-testid="recipient-results">
                    {recipients.map(r => (
                      <button key={r.id} onClick={() => { setSelectedRecipient(r); setRecipients([]); }} data-testid={`recipient-${r.id}`}
                        className="w-full flex items-center gap-3 p-3 hover:bg-emerald-50 transition-colors text-left border-b border-gray-50 last:border-0">
                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600">
                          {r.avatar_initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.full_name}</p>
                          <p className="text-xs text-gray-400 truncate">{r.email}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{countryFlag(r.country)}</span>
                          {r.kyc_verified && <span className="block text-[10px] text-emerald-500 mt-0.5">KYC</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Amount & Note */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Monto (CUALcoin)</label>
              <div className="relative">
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" min="0.01" step="0.01" data-testid="cmt-amount-input"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl text-lg font-medium focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400" />
                <span className="absolute left-4 top-3.5 text-sm font-bold text-gray-400">Cc</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Saldo disponible: {wallet.balance?.toFixed(2)} Cc</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nota (opcional)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} maxLength="200"
                placeholder="Ej: Pago por servicio, regalo..." data-testid="cmt-note-input"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400" />
            </div>
          </div>

          {/* Send Button */}
          <button onClick={sendTransfer} disabled={sending || !selectedRecipient || !amount || parseFloat(amount) <= 0}
            data-testid="cmt-send-btn"
            className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                </svg>
                Enviar CUALcoin
              </>
            )}
          </button>
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="space-y-2">
          {history.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p className="text-sm">Sin transferencias CMT aun</p>
            </div>
          ) : (
            history.map((t, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4" data-testid={`transfer-${t.transfer_id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  t.direction === 'sent' ? 'bg-red-100' : 'bg-emerald-100'
                }`}>
                  <svg className={`w-5 h-5 ${t.direction === 'sent' ? 'text-red-500 rotate-45' : 'text-emerald-500 -rotate-45'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{t.counterpart_name}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">{countryFlag(t.counterpart_country)}</span>
                    {t.is_international && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600">INT</span>}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t.note && <span className="text-gray-500">{t.note} - </span>}
                    {new Date(t.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${t.direction === 'sent' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {t.direction === 'sent' ? '-' : '+'}{t.amount.toFixed(2)} Cc
                  </p>
                  {t.fee > 0 && <p className="text-[10px] text-gray-400">Fee: {t.fee} Cc</p>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total_transfers || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Transferencias Totales</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-cyan-600">{stats.international_transfers || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Internacionales</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{(stats.total_volume || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Volumen Total (Cc)</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.countries_active || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Paises Activos</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Mis Estadisticas CMT</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Transferencias realizadas</span>
                <span className="font-medium">{userStats.transactions_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total enviado</span>
                <span className="font-medium text-red-600">{(userStats.total_sent || 0).toFixed(2)} Cc</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total recibido</span>
                <span className="font-medium text-emerald-600">{(userStats.total_received || 0).toFixed(2)} Cc</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Paises alcanzados</span>
                <span className="font-medium">{(userStats.countries_reached || []).join(', ') || 'Ninguno'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMTComponent;
